import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Clock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Skeleton } from '../components/Skeleton';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric', timeZone: 'America/Chicago'});
  } catch {
    return '';
  }
};

export default function NewsAuthorPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/news/author/${slug}`);
        if (!res.ok) {
          if (!cancelled) setNotFound(true);
          return;
        }
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch {
        if (!cancelled) setNotFound(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F9F6EF] text-[#0A1F2E]">
        <Header currentPage="news" />
        <main className="max-w-3xl mx-auto px-6 md:px-10 py-24 text-center">
          <p className="font-display italic text-3xl text-[#0A1F2E]/70 mb-4">Author not found.</p>
          <Link
            to="/relo-news"
            className="inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.12em] bg-[#0A1F2E] text-[#F9F6EF] px-5 py-2.5 rounded-full"
          >
            <ArrowLeft className="w-4 h-4" /> Relo News
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F9F6EF] text-[#0A1F2E]">
        <Header currentPage="news" />
        <main className="max-w-4xl mx-auto px-6 md:px-10 py-16 space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-6 w-full" />
        </main>
        <Footer />
      </div>
    );
  }

  const a = data.author || {};
  const articles = data.articles || [];
  const initial = (a.name || 'M').trim().charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-[#F9F6EF] text-[#0A1F2E]" data-testid="news-author-page">
      <SEO
        title={`${a.name} - Mundalo Relo News author`}
        description={a.bio || `Articles by ${a.name} on Mundalo Relo News.`}
        path={`/relo-news/author/${a.slug}`}
        image={a.photo_url || undefined}
      />
      <Header currentPage="news" />

      <main className="max-w-4xl mx-auto px-6 md:px-10 pt-14 pb-20">
        <Link
          to="/relo-news"
          className="inline-flex items-center gap-1.5 font-mono uppercase text-[11px] tracking-[0.15em] text-[#1A5C6E] hover:text-[#2CA8A0] mb-8 transition"
          data-testid="author-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Relo News
        </Link>

        <header className="flex flex-col sm:flex-row items-start gap-6 pb-10 border-b border-[#0A1F2E]/10">
          {a.photo_url ? (
            <img
              src={a.photo_url}
              alt={a.name}
              className="w-24 h-24 rounded-full object-cover bg-[#F4ECD8]"
              data-testid="author-photo"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-[#0A1F2E] text-[#F9F6EF] flex items-center justify-center font-display italic text-4xl">
              {initial}
            </div>
          )}
          <div className="flex-1">
            <p
              className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-2"
              data-testid="author-role"
            >
              {a.role || 'Mundalo Editorial Team'}
            </p>
            <h1
              className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-3"
              data-testid="author-name"
            >
              {a.name}
            </h1>
            <p
              className="font-body text-base text-[#0A1F2E]/75 leading-relaxed max-w-2xl"
              data-testid="author-bio"
            >
              {a.bio}
            </p>
            {a.links && (a.links.x || a.links.linkedin) && (
              <div className="flex items-center gap-4 mt-4">
                {a.links.x && (
                  <a
                    href={a.links.x}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#1A5C6E] hover:text-[#2CA8A0]"
                    data-testid="author-link-x"
                  >
                    X / Twitter
                  </a>
                )}
                {a.links.linkedin && (
                  <a
                    href={a.links.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#1A5C6E] hover:text-[#2CA8A0]"
                    data-testid="author-link-linkedin"
                  >
                    LinkedIn
                  </a>
                )}
              </div>
            )}
          </div>
        </header>

        <section className="mt-12">
          <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-6">
            {data.count} {data.count === 1 ? 'article' : 'articles'} by {a.name}
          </p>
          {articles.length === 0 ? (
            <p
              className="font-body text-[#0A1F2E]/60 italic"
              data-testid="author-empty"
            >
              No published articles yet - check back soon.
            </p>
          ) : (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="author-articles">
              {articles.map((art) => (
                <li
                  key={art.slug}
                  className="bg-white border border-[#EFE6CF] rounded-lg overflow-hidden"
                  data-testid={`author-article-${art.slug}`}
                >
                  <Link to={`/relo-news/${art.slug}`} className="block hover:opacity-90 transition">
                    {art.hero_image_url && (
                      <div className="aspect-[16/9] overflow-hidden bg-[#F4ECD8]">
                        <img src={art.hero_image_url} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-5">
                      <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-2">
                        {art.category_label}
                      </p>
                      <h3 className="font-display italic text-2xl text-[#0A1F2E] leading-[1.15] mb-2">
                        {art.title}
                      </h3>
                      <p className="font-body text-sm text-[#0A1F2E]/65 leading-relaxed mb-3 line-clamp-3">
                        {art.excerpt}
                      </p>
                      <div className="flex items-center gap-3 font-mono uppercase text-[10px] tracking-[0.12em] text-[#0A1F2E]/55">
                        <span>{formatDate(art.published_at)}</span>
                        {art.read_time && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {art.read_time}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}
